// Advanced Mode pure engine functions (Phase 1).
// Canonical source: design/canonical-numbers.md section 16.
// Annual demand/generation is TWh; peak demand/firm capacity is GW.

import { PLANT_TYPE } from '../data/plants.js';
import { EFFECT_KIND } from '../data/disasters.js';
import { CLEAN_ENERGY_AWARD_ELIGIBLE_SET } from '../data/awards.js';
import {
  ADVANCED_DEMAND_BY_YEAR,
  RESERVE_MARGIN,
  PEAK_SHORTFALL_TIER1_LIMIT_UNITS,
  PEAK_SHORTFALL_TIER1_RATE_PER_UNIT,
  PEAK_SHORTFALL_TIER2_RATE_PER_UNIT,
  PLANT_TYPE_STORAGE,
  VARIABLE_DIVERSITY_UPLIFT_PER_FAMILY,
  VARIABLE_DIVERSITY_UPLIFT_CAP,
  VARIABLE_FIRM_DECLINE_SCHEDULE,
  RESOURCE_DEVELOPMENT_PREMIUM_STARTS_AT_COPY,
  RESOURCE_DEVELOPMENT_PREMIUM_PER_BUILD,
  DECOMMISSION_FEE_PER_PLANT,
  YEAR_ONE_INHERITED_COAL_RETIREMENT_ADDITION,
  EARLY_CLOSURE_SURCHARGE,
  EARLY_CLOSURE_YEAR_WEIGHTS,
  ADVANCED_EOS_DUPLICATE_MULTIPLIER,
  ADVANCED_EOS_FIRST_COPY_PAYS_FULL
} from '../data/advancedCapacity.js';
import { ADVANCED_DISASTER_EFFECTS } from '../data/advancedDisasters.js';
import {
  resolveDisasterTargetIds,
  outputMultiplierFactorForPlant,
  demandAddAmountForEffect
} from './disasters.js';
import { shortfallPenalty } from './shortfall.js';
import { computeYearOpex, totalYearOpex } from './opex.js';

export function isStoragePlant(plant) {
  return plant?.type === PLANT_TYPE_STORAGE;
}

export function requiredFirmCapacity(peakDemandCapacityUnits) {
  return peakDemandCapacityUnits * RESERVE_MARGIN;
}

// Cost of retiring one plant in a given year (section 16.11): the flat
// decommissioning fee, plus an early-closure charge that falls away by
// Year 3. Priced off roster capital, so the inherited fleet - which
// cost the player nothing - does not close for free.
export function decommissionFeeForPlant(plant, year) {
  const rosterCapital = plant?.rosterCapital ?? plant?.capital ?? 0;
  const weight = EARLY_CLOSURE_YEAR_WEIGHTS[year] ?? 0;
  const inheritedCoalAddition =
    year === 1 && plant?.id === 'legacy-coal-plant'
      ? YEAR_ONE_INHERITED_COAL_RETIREMENT_ADDITION
      : 0;
  return (
    DECOMMISSION_FEE_PER_PLANT +
    EARLY_CLOSURE_SURCHARGE * weight * rosterCapital +
    inheritedCoalAddition
  );
}

export function playerBuildCounts(playerBuilds = []) {
  const counts = {};
  for (const id of playerBuilds) {
    counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

export function resourceDevelopmentPremiumForBuilds({
  newlyBuilt = [],
  priorPlayerBuilds = []
}) {
  const counts = playerBuildCounts(priorPlayerBuilds);
  let premium = 0;
  for (const id of newlyBuilt) {
    const copyNumber = (counts[id] ?? 0) + 1;
    if (copyNumber >= RESOURCE_DEVELOPMENT_PREMIUM_STARTS_AT_COPY) {
      premium += RESOURCE_DEVELOPMENT_PREMIUM_PER_BUILD;
    }
    counts[id] = copyNumber;
  }
  return premium;
}

export function buildCapitalCost({
  newlyBuilt = [],
  priorPlayerBuilds = [],
  plantsById
}) {
  const listedCapital = newlyBuilt.reduce(
    (sum, id) => sum + (plantsById[id]?.capital ?? 0),
    0
  );
  const resourceDevelopmentPremium = resourceDevelopmentPremiumForBuilds({
    newlyBuilt,
    priorPlayerBuilds
  });
  return {
    listedCapital,
    resourceDevelopmentPremium,
    effectiveCapital: listedCapital + resourceDevelopmentPremium
  };
}

export function usefulGenerationCarbonEmissions({
  positiveEmissions,
  negativeEmissions,
  cleanGeneration,
  annualDemand
}) {
  const usefulCleanFraction =
    cleanGeneration > 0
      ? Math.min(1, Math.max(0, annualDemand) / cleanGeneration)
      : 0;
  return {
    carbonEmissions:
      positiveEmissions + negativeEmissions * usefulCleanFraction,
    usefulCleanFraction
  };
}

// Peak capacity shortfall penalty (section 16.4).
export function peakShortfallPenalty(shortfallCapacityUnits) {
  if (shortfallCapacityUnits <= 0) return 0;
  if (shortfallCapacityUnits <= PEAK_SHORTFALL_TIER1_LIMIT_UNITS) {
    return shortfallCapacityUnits * PEAK_SHORTFALL_TIER1_RATE_PER_UNIT;
  }
  const tier1Cost =
    PEAK_SHORTFALL_TIER1_LIMIT_UNITS * PEAK_SHORTFALL_TIER1_RATE_PER_UNIT;
  const tier2Units =
    shortfallCapacityUnits - PEAK_SHORTFALL_TIER1_LIMIT_UNITS;
  return tier1Cost + tier2Units * PEAK_SHORTFALL_TIER2_RATE_PER_UNIT;
}

// Energy shortfall reuses the Classic schedule (TWh in
// place of TWh).
export const energyShortfallPenalty = shortfallPenalty;

// Fixed ex-ante planning credit (section 16.3).
export function baseFirmCapacityUnits(plant) {
  return (plant.capacityUnits ?? 0) * (plant.firmCapacityCredit ?? 0);
}

// Firm-channel softening (section 16.6). Firm capacity is a
// planning credit for the peak hour: a partial output year dents it
// half as much as it dents annual energy. Offline stays offline.
export function firmFactorFromGenerationFactor(generationFactor) {
  if (generationFactor <= 0) return 0;
  return (1 + generationFactor) / 2;
}

// The distinct variable families in a portfolio. Split out from the
// uplift below so the build screen can name them without re-deriving
// the rule — passed the whole palette, it also answers how many exist.
export function variableFamiliesOwned(portfolio, plantsById) {
  const families = new Set();
  for (const id of new Set(portfolio)) {
    const p = plantsById[id];
    if (!p || isStoragePlant(p)) continue;
    if (p.type === PLANT_TYPE.VARIABLE && p.family) families.add(p.family);
  }
  return families;
}

// Statistical firming (section 16.9). Each distinct variable family
// beyond the first raises the firm credit of every variable plant:
// uncorrelated variable resources firm each other.
export function variableDiversityUplift(portfolio, plantsById) {
  const families = variableFamiliesOwned(portfolio, plantsById);
  if (families.size <= 1) return 0;
  return Math.min(
    (families.size - 1) * VARIABLE_DIVERSITY_UPLIFT_PER_FAMILY,
    VARIABLE_DIVERSITY_UPLIFT_CAP
  );
}

// Base firm capacity including the diversity uplift for variable
// plants. Storage and reliable plants are unaffected by the uplift.
export function upliftedFirmCapacityUnits(plant, diversityUplift) {
  if (
    diversityUplift > 0 &&
    !isStoragePlant(plant) &&
    plant.type === PLANT_TYPE.VARIABLE
  ) {
    return (
      (plant.capacityUnits ?? 0) *
      ((plant.firmCapacityCredit ?? 0) + diversityUplift)
    );
  }
  return baseFirmCapacityUnits(plant);
}

// Expected annual generation is derived from existing output data,
// never stored independently (section 16.2).
export function expectedAnnualGeneration(plant) {
  if (isStoragePlant(plant)) return 0;
  if (plant.type === PLANT_TYPE.RELIABLE) return plant.output;
  return (plant.output.min + plant.output.max) / 2;
}

export function expectedPortfolioGeneration(portfolio, plantsById) {
  return portfolio.reduce(
    (s, id) => s + expectedAnnualGeneration(plantsById[id]),
    0
  );
}

// Physical units of each storage type in a portfolio. Counts every
// unit, not every distinct type: two batteries take twice the energy
// to fill and deliver twice the firm capacity.
export function storageUnitCounts(portfolio, plantsById) {
  const counts = {};
  for (const id of portfolio) {
    if (!isStoragePlant(plantsById[id])) continue;
    counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

// Surplus a store must draw to gain one unit of held charge. Same-year
// stores pay the round-trip loss on the way in; inter-year carriers pay
// it when the charge crosses into the next year (section 16.5.2), so
// charging them is priced raw.
function surplusPerStoredUnit(plant) {
  return plant.carriesEnergyBetweenYears ? 1 : 1 / plant.roundTripEfficiency;
}

// Expected surplus needed to fill a store from empty, in TWh. This is
// the honest comparison between the two technologies, not
// roundTripEfficiency: that field is a charge-time loss for a
// same-year store and a carry-time loss for a carrier, so the two
// percentages are not measuring the same thing (section 16.5).
export function storageSurplusToFill(plant, count = 1) {
  return plant.storageEnergyUnits * count * surplusPerStoredUnit(plant);
}

// Storage charging rule (section 16.5). Uses expected pre-disaster
// surplus, and fills in priority order: the store returning the most
// firm capacity per TWh of surplus fills first, then the next. Sharing
// a pool pro rata instead let an added store dilute one already full,
// which could cut total firm capacity.
//
// Returns one row per storage plant type, in the order they charge,
// carrying everything a caller might want to show: the charge level,
// and how much more expected surplus it would take to fill this store
// **given the queue ahead of it**. Storage UI reads this rather than
// re-deriving the merit order, so there is one implementation of the
// rule.
//
// `carriedChargeUnits` is the unit count the carry was measured on.
// If the fleet has shrunk since, the carry is scaled down first
// (section 16.5.2): retiring one of two units destroys that unit's
// share rather than concentrating it into the survivor. Omitting it
// disables the scaling, which is correct when no retirement can have
// happened.
export function storageChargeAllocation({
  expectedSurplus,
  portfolio,
  plantsById,
  carriedCharge = {},
  carriedChargeUnits = {}
}) {
  const counts = storageUnitCounts(portfolio, plantsById);

  const rows = Object.entries(counts).map(([id, count]) => {
    const p = plantsById[id];
    const capacity = p.storageEnergyUnits * count;
    const perUnit = surplusPerStoredUnit(p);
    const firmAtFull = p.capacityUnits * p.firmCapacityCredit * count;
    const countAtCarry = carriedChargeUnits[id] ?? count;
    const survivingShare =
      countAtCarry > 0 ? Math.min(1, count / countAtCarry) : 1;
    const carriedIn = Math.max(0, carriedCharge[id] ?? 0) * survivingShare;
    return {
      id,
      name: p.name,
      count,
      capacity,
      perUnit,
      held: Math.min(carriedIn, capacity),
      firmPerSurplus: capacity > 0 ? firmAtFull / capacity / perUnit : 0
    };
  });
  // Ties fall back to plant id so the order is at least deterministic;
  // if a rebalance ever makes two rates equal, give them a deliberate
  // order rather than relying on this (section 16.5.1).
  rows.sort(
    (a, b) =>
      b.firmPerSurplus - a.firmPerSurplus || a.id.localeCompare(b.id)
  );

  let remaining = Math.max(0, expectedSurplus);
  // Surplus still unmet by everything ahead of this row. A store can
  // only fill once the queue in front of it has, so its shortfall is
  // cumulative, not its own gap in isolation.
  let deficitAhead = 0;
  return rows.map((row) => {
    if (row.capacity <= 0) {
      return { ...row, level: 0, taken: 0, surplusShort: 0 };
    }
    const wanted = Math.max(0, (row.capacity - row.held) * row.perUnit);
    const taken = Math.min(remaining, wanted);
    remaining -= taken;
    deficitAhead += wanted - taken;
    return {
      ...row,
      level: (row.held + taken / row.perUnit) / row.capacity,
      taken,
      surplusShort: deficitAhead
    };
  });
}

// Charge level between 0 and 1 per storage plant type, counting
// carried-in charge (which costs no surplus this year).
export function storageChargeLevels(args) {
  const levels = {};
  for (const row of storageChargeAllocation(args)) {
    levels[row.id] = row.level;
  }
  return levels;
}

function advancedBehaviourFor(disaster) {
  return (
    disaster.advanced ??
    ADVANCED_DISASTER_EFFECTS[disaster.id] ??
    {}
  );
}

// Advanced disaster resolution (section 16.6). Each disaster can
// touch annual generation, firm capacity, annual energy demand,
// peak demand, cost, and policy state independently. Multipliers
// stack multiplicatively per channel, as in Classic Mode.
export function computeAdvancedDisasterEffects({
  disasters,
  portfolio,
  plantsById,
  rng = null,
  year = null
}) {
  const generationMultipliers = {};
  const firmCapacityMultipliers = {};
  const affectedPlantIdsByDisasterId = {};
  let energyDemandAdd = 0;
  let peakDemandAdd = 0;
  let priceShock = false;

  for (const d of disasters) {
    const adv = advancedBehaviourFor(d);
    if (d.effect.kind === EFFECT_KIND.OUTPUT_MULTIPLIER) {
      const ids = resolveDisasterTargetIds({
        disaster: d,
        portfolio,
        plantsById,
        rng
      });
      affectedPlantIdsByDisasterId[d.id] = ids;
      for (const id of ids) {
        const generationFactor = outputMultiplierFactorForPlant(
          d.effect,
          id
        );
        if (adv.affectsGeneration !== false) {
          generationMultipliers[id] =
            (generationMultipliers[id] ?? 1) * generationFactor;
        }
        if (adv.affectsFirmCapacity !== false) {
          // Softened by default (section 16.6). A per-card override
          // bypasses the softening rule, either as a fixed factor or
          // by reusing the (possibly per-plant) generation factor
          // for cards that force real outages at peak.
          const firmFactor = adv.firmUsesGenerationFactor
            ? generationFactor
            : adv.firmCapacityFactor ??
              firmFactorFromGenerationFactor(generationFactor);
          firmCapacityMultipliers[id] =
            (firmCapacityMultipliers[id] ?? 1) * firmFactor;
        }
      }
    } else if (d.effect.kind === EFFECT_KIND.OPEX_MULTIPLIER) {
      priceShock = true;
    } else if (d.effect.kind === EFFECT_KIND.DEMAND_ADD) {
      energyDemandAdd += demandAddAmountForEffect(d.effect, year);
      if (adv.peakDemandAddByYear && year !== null && year !== undefined) {
        peakDemandAdd += adv.peakDemandAddByYear[year] ?? 0;
      }
    }
    // Firm-capacity-only derates, independent of the Classic effect
    // kind (heatwave thermal derate, price-shock fuel squeeze). One
    // or more { families, factor } entries per card.
    if (adv.firmDerate) {
      const derates = Array.isArray(adv.firmDerate)
        ? adv.firmDerate
        : [adv.firmDerate];
      for (const derate of derates) {
        const families = new Set(derate.families ?? []);
        for (const id of new Set(portfolio)) {
          const p = plantsById[id];
          if (p?.family && families.has(p.family)) {
            firmCapacityMultipliers[id] =
              (firmCapacityMultipliers[id] ?? 1) * derate.factor;
          }
        }
      }
    }
  }

  return {
    generationMultipliers,
    firmCapacityMultipliers,
    affectedPlantIdsByDisasterId,
    energyDemandAdd,
    peakDemandAdd,
    priceShock
  };
}

// Actual annual generation per plant type in TWh. Storage
// generates nothing; reliable plants use their fixed output;
// variable plants use the year's roll. Generation disaster
// multipliers apply on top.
export function computeAdvancedGenerationPerType({
  portfolio,
  plantsById,
  rolledOutputs,
  generationMultipliers
}) {
  const outputs = {};
  for (const id of new Set(portfolio)) {
    const p = plantsById[id];
    let base;
    if (isStoragePlant(p)) {
      base = 0;
    } else if (p.type === PLANT_TYPE.RELIABLE) {
      base = p.output;
    } else {
      base = rolledOutputs[id];
    }
    outputs[id] = base * (generationMultipliers[id] ?? 1);
  }
  return outputs;
}

// How many "full credit" units each plant type is worth once the
// declining schedule has been applied within its variable family.
//
// A reliable plant or a store always contributes its whole count. A
// variable plant contributes the sum of the schedule factors landing
// on its instances - so three Solar PV under a [1, 0.6, 0.4] schedule
// weigh 2.0, not 3.
//
// Sorting biggest-unit-first inside each family makes the schedule
// price the marginal copies and makes the answer independent of build
// order; ties break on id so it is fully deterministic.
export function firmCapacityCountWeights({
  portfolio,
  plantsById,
  schedule = VARIABLE_FIRM_DECLINE_SCHEDULE
}) {
  const weights = {};
  const byFamily = new Map();
  for (const id of portfolio) {
    const p = plantsById[id];
    if (!p) continue;
    const declines =
      !isStoragePlant(p) && p.type === PLANT_TYPE.VARIABLE && Boolean(p.family);
    if (!declines) {
      weights[id] = (weights[id] ?? 0) + 1;
      continue;
    }
    if (!byFamily.has(p.family)) byFamily.set(p.family, []);
    byFamily.get(p.family).push(id);
  }
  for (const ids of byFamily.values()) {
    const ordered = [...ids].sort((a, b) => {
      const delta =
        baseFirmCapacityUnits(plantsById[b]) -
        baseFirmCapacityUnits(plantsById[a]);
      return delta !== 0 ? delta : a.localeCompare(b);
    });
    ordered.forEach((id, index) => {
      const factor =
        schedule[Math.min(index, schedule.length - 1)] ?? 1;
      weights[id] = (weights[id] ?? 0) + factor;
    });
  }
  return weights;
}

// Actual firm capacity per plant type in GW, TOTALLED across every
// copy owned. Fixed ex-ante credit plus the diversity uplift, less the
// declining schedule within a variable family, modified only by
// disaster/constraint multipliers and the storage charge level — never
// by the output roll.
//
// This returns TOTALS, not a per-unit figure. It used to return
// per-unit and be summed once per instance by the caller; a
// count-dependent schedule cannot be expressed that way, and a stray
// multiply-by-count is exactly the silent halving or doubling this
// refactor had to avoid. The field is named `firmCapacityTotalByType`
// throughout so any consumer still expecting per-unit fails loudly.
export function computeAdvancedFirmCapacityTotalByType({
  portfolio,
  plantsById,
  firmCapacityMultipliers,
  chargeLevels = {},
  diversityUplift = 0,
  declineSchedule = VARIABLE_FIRM_DECLINE_SCHEDULE
}) {
  const weights = firmCapacityCountWeights({
    portfolio,
    plantsById,
    schedule: declineSchedule
  });
  const firm = {};
  for (const id of new Set(portfolio)) {
    const p = plantsById[id];
    let value = upliftedFirmCapacityUnits(p, diversityUplift);
    if (isStoragePlant(p)) value *= chargeLevels[id] ?? 0;
    firm[id] = value * (firmCapacityMultipliers[id] ?? 1) * (weights[id] ?? 0);
  }
  return firm;
}

// Charge carried into next year (section 16.5.2). Only inter-year
// carriers hold charge across a year, and they pay their round-trip
// loss on the way: charge is worth 70% of itself a year later, so
// hoarding decays. Grid Battery starts every year empty.
//
// Returns held charge in TWh per plant type, keyed for the next year's
// storageChargeLevels call.
export function computeCarriedChargeForNextYear({
  portfolio,
  plantsById,
  chargeLevels = {}
}) {
  const counts = {};
  for (const id of portfolio) {
    const p = plantsById[id];
    if (!isStoragePlant(p) || !p.carriesEnergyBetweenYears) continue;
    counts[id] = (counts[id] ?? 0) + 1;
  }
  const carried = {};
  for (const [id, count] of Object.entries(counts)) {
    const p = plantsById[id];
    const held = (chargeLevels[id] ?? 0) * p.storageEnergyUnits * count;
    carried[id] = held * p.roundTripEfficiency;
  }
  return carried;
}

// Unit count each carried-charge figure was measured on, so next
// year can tell a shrunken fleet from a grown one (section 16.5.2).
export function computeCarriedChargeUnitsForNextYear({
  portfolio,
  plantsById
}) {
  const counts = {};
  for (const id of portfolio) {
    const p = plantsById[id];
    if (!isStoragePlant(p) || !p.carriesEnergyBetweenYears) continue;
    counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

// The Just Transition bonus (section 16.13) lived here and is removed
// at v1.0 - see the note in src/data/advancedCapacity.js. Retiring the
// fossil fleet is now rewarded only through the fossil-free tier of
// the System Resilience Award, and priced by the early-closure charge.

// A shortfall is a sum of floating-point terms subtracted from another,
// so a plan that exactly meets its demand can land a hair either side
// of zero - 1.78e-15 GW was observed when the firm-capacity summation
// order changed. The cost of that is nothing, but a strict "> 0" test
// reads it as a genuine miss, and the Affordability Award's peak gate
// is exactly such a test: a $21M award was being cancelled by float
// residue in 10 games out of 2,000.
//
// Anything below this tolerance is exactly zero. Real shortfalls are
// orders of magnitude larger - the smallest meaningful one is a
// fraction of a GW - so nothing real is swallowed, and the result stops
// depending on the order the engine happens to add its terms up.
const SHORTFALL_EPSILON = 1e-9;

function clampShortfall(value) {
  return value > SHORTFALL_EPSILON ? value : 0;
}

function sumPerPlant(portfolio, perType) {
  let total = 0;
  for (const id of portfolio) total += perType[id] ?? 0;
  return total;
}

// Resolve one Advanced Capacity Mode year. Mirrors the Classic
// resolveYear flow but checks both adequacy layers.
export function resolveAdvancedYear({
  year,
  portfolio,
  newlyBuiltThisYear,
  priorPlayerBuilds = [],
  subsidyActive,
  disasters,
  rolledOutputs,
  plantsById,
  rng = null,
  retiredThisYear = [],
  carriedCharge = {},
  carriedChargeUnits = {},
  // Overridable so calibration can sweep the schedule without editing
  // src/data, the same way `plantsById` lets it sweep a stat line.
  declineSchedule = VARIABLE_FIRM_DECLINE_SCHEDULE
}) {
  const baseDemand = ADVANCED_DEMAND_BY_YEAR[year];

  const {
    generationMultipliers,
    firmCapacityMultipliers,
    affectedPlantIdsByDisasterId,
    energyDemandAdd,
    peakDemandAdd,
    priceShock
  } = computeAdvancedDisasterEffects({
    disasters,
    portfolio,
    plantsById,
    rng,
    year
  });

  const generationPerType = computeAdvancedGenerationPerType({
    portfolio,
    plantsById,
    rolledOutputs,
    generationMultipliers
  });
  const actualAnnualGeneration = sumPerPlant(portfolio, generationPerType);

  // Storage charging uses expected pre-disaster generation against
  // base annual demand (planning quantities, section 16.5).
  const expectedGeneration = expectedPortfolioGeneration(
    portfolio,
    plantsById
  );
  const expectedSurplus = expectedGeneration - baseDemand.energy;
  const chargeLevels = storageChargeLevels({
    expectedSurplus,
    portfolio,
    plantsById,
    carriedCharge,
    carriedChargeUnits
  });

  const diversityUplift = variableDiversityUplift(portfolio, plantsById);
  const firmCapacityTotalByType = computeAdvancedFirmCapacityTotalByType({
    portfolio,
    plantsById,
    firmCapacityMultipliers,
    chargeLevels,
    diversityUplift,
    declineSchedule
  });
  // Already totalled per type, so this sums the map rather than
  // walking the portfolio - adding once per instance would count every
  // plant twice.
  const actualFirmCapacityUnits = Object.values(
    firmCapacityTotalByType
  ).reduce((sum, value) => sum + value, 0);

  // Award aggregates (storage excluded; it generates nothing and
  // does not count towards award family diversity).
  let cleanEnergyOutput = 0;
  let variableCleanEnergyOutput = 0;
  const familyOutput = {};
  for (const id of portfolio) {
    const p = plantsById[id];
    if (isStoragePlant(p)) continue;
    const output = generationPerType[id] ?? 0;
    if (CLEAN_ENERGY_AWARD_ELIGIBLE_SET.has(id)) {
      cleanEnergyOutput += output;
      if (p.type === PLANT_TYPE.VARIABLE) {
        variableCleanEnergyOutput += output;
      }
    }
    if (p.family) {
      familyOutput[p.family] = (familyOutput[p.family] ?? 0) + output;
    }
  }

  const annualEnergyDemand = baseDemand.energy + energyDemandAdd;
  const peakDemandCapacityUnits = baseDemand.peak + peakDemandAdd;
  const requiredFirmCapacityUnits = requiredFirmCapacity(
    peakDemandCapacityUnits
  );

  // Storage never touches the energy layer (section 16.5): it holds
  // charge for the peak hour and nothing else.
  const energyShortfall = clampShortfall(
    annualEnergyDemand - actualAnnualGeneration
  );
  const energyShortfallCost = energyShortfallPenalty(energyShortfall);

  const carriedChargeForNextYear = computeCarriedChargeForNextYear({
    portfolio,
    plantsById,
    chargeLevels
  });
  const carriedChargeUnitsForNextYear = computeCarriedChargeUnitsForNextYear({
    portfolio,
    plantsById
  });

  const capacityShortfall = clampShortfall(
    requiredFirmCapacityUnits - actualFirmCapacityUnits
  );
  const capacityShortfallCost = peakShortfallPenalty(capacityShortfall);

  const opexRows = computeYearOpex({
    portfolio,
    plantsById,
    subsidyActive,
    priceShockActive: priceShock,
    eosDuplicateMultiplier: ADVANCED_EOS_DUPLICATE_MULTIPLIER,
    eosFirstCopyPaysFull: ADVANCED_EOS_FIRST_COPY_PAYS_FULL
  });
  const opex = totalYearOpex(opexRows);

  const {
    listedCapital: capital,
    resourceDevelopmentPremium,
    effectiveCapital
  } = buildCapitalCost({
    newlyBuilt: newlyBuiltThisYear,
    priorPlayerBuilds,
    plantsById
  });
  let positiveEmissions = 0;
  let negativeEmissions = 0;
  for (const id of portfolio) {
    const value = plantsById[id].emissions;
    if (value > 0) positiveEmissions += value;
    if (value < 0) negativeEmissions += value;
  }
  const emissions = positiveEmissions + negativeEmissions;
  const { carbonEmissions, usefulCleanFraction } =
    usefulGenerationCarbonEmissions({
      positiveEmissions,
      negativeEmissions,
      cleanGeneration: cleanEnergyOutput,
      annualDemand: annualEnergyDemand
    });

  // Retirement (section 16.11): the retired plants have already been
  // removed from the portfolio by the caller; this prices the fee.
  const decommissionCost = retiredThisYear.reduce(
    (s, id) => s + decommissionFeeForPlant(plantsById[id], year),
    0
  );

  return {
    year,
    capital,
    resourceDevelopmentPremium,
    effectiveCapital,
    opex,
    retiredThisYear,
    decommissionCost,
    carriedCharge,
    carriedChargeUnits,
    carriedChargeForNextYear,
    carriedChargeUnitsForNextYear,
    diversityUplift,
    emissions,
    positiveEmissions,
    negativeEmissions,
    carbonEmissions,
    usefulCleanFraction,
    annualEnergyDemand,
    actualAnnualGeneration,
    expectedGeneration,
    expectedSurplus,
    energyShortfall,
    energyShortfallCost,
    peakDemandCapacityUnits,
    requiredFirmCapacityUnits,
    actualFirmCapacityUnits,
    capacityShortfall,
    capacityShortfallCost,
    storageChargeLevels: chargeLevels,
    cleanEnergyOutput,
    variableCleanEnergyOutput,
    familyOutput,
    generationPerType,
    firmCapacityTotalByType,
    affectedPlantIdsByDisasterId,
    energyDemandAdd,
    peakDemandAdd,
    priceShock,
    subsidyActive,
    disasters,
    rolledOutputs,
    opexRows
  };
}
